import { useEffect, useState } from 'react';
import { Home, BarChart2, Code, Briefcase, Cpu, MessageSquare, Sun, Moon, Send } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { TSizes } from '../constants/sizes';
import { GlassContainer } from './GlassContainer';

const SECTIONS = [
  { title: 'Home', icon: Home },
  { title: 'Statistics', icon: BarChart2 },
  { title: 'Projects', icon: Code },
  { title: 'Services', icon: Briefcase },
  { title: 'Skills', icon: Cpu },
  { title: 'Contact', icon: MessageSquare },
];

export const THeader = ({ currentIndex = 0, onSectionTap }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 760);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: isMobile ? '16px 16px' : '20px 32px',
      transition: 'padding 0.3s ease',
      display: 'flex',
      justifyContent: 'center',
    }}>

      <GlassContainer
        width="100%"
        height="auto"
        radius={TSizes.radiusPill}
        clickable={false}
        style={{ maxWidth: '1240px' }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '8px 12px' : '10px 20px',
          boxSizing: 'border-box',
          width: '100%'
        }}>

          <div style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, var(--primary), #8250DF)`,
            boxShadow: `0 4px 14px ${isDark ? 'rgba(31, 111, 235, 0.4)' : 'rgba(9, 105, 218, 0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: `${TSizes.fontSizeLg}px`,
            userSelect: 'none',
          }}>
            T
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', marginLeft: '12px' }}>
            {!isMobile && (
              <nav style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                gap: '4px',
                padding: '0 8px',
              }}>
                {SECTIONS.map((section, index) => {
                  const IconComponent = section.icon;
                  const isSelected = currentIndex === index;
                  return (
                    <button
                      key={section.title}
                      onClick={() => onSectionTap?.(index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        borderRadius: `${TSizes.radiusPill}px`,
                        backgroundColor: isSelected ? (isDark ? 'rgba(56, 139, 253, 0.15)' : 'rgba(9, 105, 218, 0.09)') : 'transparent',
                        border: `1px solid ${isSelected ? (isDark ? 'rgba(56, 139, 253, 0.25)' : 'rgba(9, 105, 218, 0.15)') : 'transparent'}`,
                        color: isSelected ? (isDark ? '#58A6FF' : 'var(--primary)') : 'var(--text-secondary)',
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: `${TSizes.fontSizeSm}px`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                      }}
                    >
                      <IconComponent size={15} style={{ opacity: isSelected ? 1 : 0.7 }} />
                      {section.title}
                    </button>
                  );
                })}
              </nav>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>

            <button
              onClick={toggleTheme}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                backgroundColor: 'transparent',
                border: `1px solid ${isDark ? 'rgba(240, 246, 252, 0.1)' : 'rgba(31, 35, 40, 0.1)'}`,
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {!isMobile ? (
              <button
                onClick={() => onSectionTap?.(5)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: `${TSizes.radiusPill}px`,
                  backgroundColor: 'var(--primary)',
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: `${TSizes.fontSizeSm}px`,
                  cursor: 'pointer',
                  boxShadow: `0 4px 14px ${isDark ? 'rgba(31, 111, 235, 0.35)' : 'rgba(9, 105, 218, 0.25)'}`,
                  outline: 'none',
                  transition: 'transform 0.2s ease, filter 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
              >
                <Send size={15} />
                Let's Talk
              </button>
            ) : (
              <button
                onClick={() => onSectionTap?.(5)}
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  backgroundColor: isDark ? 'rgba(31, 111, 235, 0.15)' : 'rgba(9, 105, 218, 0.1)',
                  border: `1px solid ${isDark ? 'rgba(56, 139, 253, 0.25)' : 'rgba(9, 105, 218, 0.15)'}`,
                  color: isDark ? '#58A6FF' : 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <MessageSquare size={18} />
              </button>
            )}
          </div>

        </div>
      </GlassContainer>

    </header>
  );
};
